"use client";

/**
 * Authentication Context Provider
 * Manages authentication state using Bearer tokens from Dashboard Principal
 */

import React, { createContext, useContext, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { getDashboardUrl } from "@/lib/dashboard-url";
import { setupFetchInterceptor } from "@/lib/fetch-interceptor";

interface User {
  name: string;
  email: string;
  image?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Hook to access authentication context
 * Must be used within an AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Authentication Provider Component
 * Handles token detection, validation, and authentication state
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const pathname = usePathname();

  useEffect(() => {
    // Install fetch interceptor once
    setupFetchInterceptor();

    const checkAuth = async () => {
      const dashboardUrl = getDashboardUrl();
      const moduleId = process.env.NEXT_PUBLIC_MODULE_ID || "system-forms";

      console.log("[AuthProvider] Checking authentication...");

      // Step 1: Check for token in URL (from iframe or redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get("token");

      if (tokenFromUrl) {
        console.log("[AuthProvider] Token found in URL, saving to localStorage");
        localStorage.setItem("dashboard-auth-token", tokenFromUrl);
        
        // Remove token from URL for security
        urlParams.delete("token");
        const newUrl = urlParams.toString()
          ? `${window.location.pathname}?${urlParams.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }

      // Step 2: Get token from localStorage
      const token = localStorage.getItem("dashboard-auth-token");

      if (!token) {
        console.log("[AuthProvider] No token found, redirecting to login");
        setIsLoading(false);
        
        // Redirect to dashboard login with callback
        const callbackUrl = `${window.location.origin}/auth/callback?destination=${encodeURIComponent(pathname)}`;
        window.location.href = `${dashboardUrl}/login?callbackUrl=${encodeURIComponent(callbackUrl)}`;
        return;
      }

      // Step 3: Validate token with dashboard
      try {
        console.log("[AuthProvider] Validating token with dashboard");
        const response = await fetch(
          `${dashboardUrl}/api/auth/verify?moduleId=${moduleId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();

        if (!data.authenticated || !data.user) {
          console.log("[AuthProvider] Invalid token, clearing and redirecting");
          
          // Token is invalid, clear and redirect
          localStorage.removeItem("dashboard-auth-token");
          
          const callbackUrl = `${window.location.origin}/auth/callback?destination=${encodeURIComponent(pathname)}`;
          window.location.href = `${dashboardUrl}/login?callbackUrl=${encodeURIComponent(callbackUrl)}`;
          return;
        }

        // Success - user is authenticated
        console.log("[AuthProvider] User authenticated:", data.user.email);
        setUser(data.user);
        setIsAuthenticated(true);
        setIsLoading(false);
      } catch (error) {
        console.error("[AuthProvider] Error validating token:", error);
        
        // On error, clear token and redirect
        localStorage.removeItem("dashboard-auth-token");
        
        const callbackUrl = `${window.location.origin}/auth/callback?destination=${encodeURIComponent(pathname)}`;
        window.location.href = `${dashboardUrl}/login?callbackUrl=${encodeURIComponent(callbackUrl)}`;
      }
    };

    checkAuth();
  }, [pathname]);

  const logout = () => {
    console.log("[AuthProvider] Logging out");
    localStorage.removeItem("dashboard-auth-token");
    setUser(null);
    setIsAuthenticated(false);
    
    // Redirect to dashboard
    const dashboardUrl = getDashboardUrl();
    window.location.href = `${dashboardUrl}/login`;
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

